// The command bar's language: an Altekore code, optionally followed by what to open it with.
//   "fs03"          the balance sheet
//   "FS03 q2"       … as at the end of Q2 of this financial year (April–March)
//   "fs02 fy"       profit and loss for this financial year; "lfy" last year; "m" / "lm" this / last month
//   "fs02 oct"      … for October (the latest October up to today); "fs02 oct 2025" a given year
// Anything that isn't a code is a search.
export interface Period { from: string; to: string; label: string }

const iso = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const lastDay = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const fmt = (s: string) => new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export function parseCommand(input: string): { code: string | null; arg: string } {
  const m = /^\s*([a-z]{2}\d{2})(?:\s+(.*))?$/i.exec(input);
  return m ? { code: m[1].toUpperCase(), arg: (m[2] ?? "").trim() } : { code: null, arg: input.trim() };
}

// today: yyyy-mm-dd. Financial years run April to March.
export function periodFromArg(arg: string, today: string): Period | null {
  const a = arg.trim().toLowerCase().replace(/\s+/g, " ");
  if (!a) return null;
  const [ty, tm] = today.split("-").map(Number);
  const t = { y: ty, m: tm - 1 };
  const fyStart = t.m >= 3 ? t.y : t.y - 1;
  const range = (y0: number, m0: number, y1: number, m1: number, label: string): Period =>
    ({ from: iso(y0, m0, 1), to: iso(y1, m1, lastDay(y1, m1)), label: `${label}: ${fmt(iso(y0, m0, 1))} – ${fmt(iso(y1, m1, lastDay(y1, m1)))}` });
  const fyLabel = (y: number) => `FY ${y}–${String((y + 1) % 100).padStart(2, "0")}`;

  if (a === "fy" || a === "year") return range(fyStart, 3, fyStart + 1, 2, fyLabel(fyStart));
  if (a === "lfy" || a === "last year") return range(fyStart - 1, 3, fyStart, 2, fyLabel(fyStart - 1));
  if (a === "m" || a === "month") return range(t.y, t.m, t.y, t.m, "This month");
  if (a === "lm" || a === "last month") { const d = new Date(t.y, t.m - 1, 1); return range(d.getFullYear(), d.getMonth(), d.getFullYear(), d.getMonth(), "Last month"); }
  let m = /^q([1-4])(?: (\d{4}))?$/.exec(a);
  if (m) {
    const q = Number(m[1]), y = m[2] ? Number(m[2]) : fyStart;            // Q1 = Apr–Jun of the financial year starting in y
    const sm = 3 + (q - 1) * 3, sy = sm > 11 ? y + 1 : y, s = sm % 12;
    return range(sy, s, sy, s + 2, `Q${q} ${fyLabel(y)}`);
  }
  m = /^([a-z]{3})[a-z]*(?: (\d{4}))?$/.exec(a);
  if (m && MONTHS.includes(m[1])) {
    const mi = MONTHS.indexOf(m[1]);
    const y = m[2] ? Number(m[2]) : (mi <= t.m ? t.y : t.y - 1);
    const name = new Date(y, mi, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    return { ...range(y, mi, y, mi, name), label: name };
  }
  return null;
}
